import { db } from "../firebase";
import {
    ref,
    update,
    increment,
    serverTimestamp,
    DatabaseReference,
    get,
    child
} from "firebase/database";
import { HeartBank } from "./HeartBank";
import { isProduction } from "../components/global";

// Use a TypeScript union type for type safety
export type API = "dynamicmaps" | "geocoding" | "places1" | "places2";

/**
 * The data structure we'll use in Firebase.
 * This is just for your reference.
 *
 * /quotas
 *   /geocoding
 *     count: 123
 *     lastUpdated: 167... (timestamp)
 *   /places1
 *     count: 456
 *     lastUpdated: 167... (timestamp)
 */

// maps api are very expensive!! need to be careful
// billing reports lags a few hours, so be careful!
// TODO: there are other hidden maps api quotas: check weekly
// TODO: later bc cheap: cloud functions, etc
// 1000 cushion for local testing and the unexpected
export class Quotas {
    private heartbank = new HeartBank();
    private db: DatabaseReference; // The root reference for our tracking data

    private dynamicmaps = 9000;
    // 10K per month free
    // 0.7 cent afterwards

    private geocoding = 9000;
    // 10K per month free
    // 0.5 cent afterwards

    private places2 = 4000; // API Text Search Pro
    // 5K per month free
    // 3.2 cents afterwards

    private places1 = 4000; // Nearby Search
    // 5K per month free
    // 3.2 cents afterwards // $32 per 1K calls

    constructor() {
        this.db = ref(db);
    }

    /**
     * Atomically increments the counter for a specific API in the Firebase Realtime Database.
     * @param api The name of the API to track ('geocoder' or 'places').
     */
    async increment(api: API): Promise<void> {
        //console.log(`Incrementing counter for ${api} API...`);

        // We use `update` to modify multiple locations at once.
        // This is how we can update the count and a 'lastUpdated' timestamp together.
        const updates: { [key: string]: any } = {};

        // Define the paths and the values to write
        // Path: /quotas/[api]/count
        // Value: A special server value that increments the current number by 1
        updates[`/quotas/${api}/count`] = increment(1);

        // Path: /quotas/[api]/lastUpdated
        // Value: A special server value for the current server timestamp
        updates[`/quotas/${api}/lastUpdated`] = serverTimestamp();

        try {
            await update(this.db, updates);
            //console.log(`Successfully incremented ${api} counter.`);
        } catch (error) {
            console.error(`Failed to increment ${api} counter:`, error);
            // You might want to handle this error, e.g., by logging it to a monitoring service
        }
    }

    /**
     * Retrieves the current count for a specific API from the Firebase Realtime Database.
     * @param api The name of the API to get the count for.
     * @returns A promise that resolves to the current count, or 0 if it doesn't exist or an error occurs.
     */
    async getCount(api: API): Promise<number> {
        //console.log(`Getting count for ${api} API...`);
        try {
            const snapshot = await get(child(this.db, `quotas/${api}/count`));

            if (snapshot.exists()) {
                const count = snapshot.val() as number;
                console.log(`Current count for ${api} is:`, count);
                return count;
            } else {
                // If the node doesn't exist, it means the count is effectively 0
                console.log(`No count found for ${api}, assuming 0`);
                return 0;
            }
        } catch (error) {
            console.error(`Failed to get ${api} counter:`, error);
            // Return 0 as a safe default in case of a read failure
            return -1;
        }
    }

    async isFree(api: API): Promise<boolean> {
        const count = await this.getCount(api);
        if (count === -1) return false; // error: skip and try again

        let isFree, isAlertAdmin;
        switch (api) {
            case "dynamicmaps":
                isFree = isProduction ? count < this.dynamicmaps : true;
                isAlertAdmin = count === this.dynamicmaps;
                break;
            case "geocoding":
                isFree = isProduction ? count < this.geocoding : true;
                isAlertAdmin = count === this.geocoding;
                break;
            case "places1":
                isFree = isProduction ? count < this.places1 : true;
                isAlertAdmin = count === this.places1;
                break;
            case "places2":
                isFree = isProduction ? count < this.places2 : true;
                isAlertAdmin = count === this.places2;
                break;
        }

        await this.increment(api);
        if (!isFree) console.warn(`Exceeded free quota for ${api}:`, count);
        if (isAlertAdmin) this.heartbank.alertAdmin(`quotas/${api}/${count}`);
        return isFree; // always true when local
    }
}
